import { zodResolver } from '@hookform/resolvers/zod'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { CalendarClock, Search, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toApiError } from '@/api/errors'
import { LoadingState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { Alert } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { humanize } from '@/utils/format'
import { availabilityQuery, useClearSlot, useSaveSlot } from '../api'
import { slotSchema, type SlotFormValues } from '../schemas'
import type { Lesson, Period, SectionGrid } from '../types'
import { timeRange, weekdayName } from '../week'
import { ResourcePicker, type PickerOption } from './ResourcePicker'

interface CellDialogProps {
  grid: SectionGrid
  dayOfWeek: number
  period: Period
  lesson?: Lesson
  onOpenChange: (open: boolean) => void
}

const FIELDS = ['subjectId', 'teacherId', 'roomId']

/**
 * Edits one cell of a class timetable. Teachers and rooms come from the availability endpoint, so
 * whoever is already booked elsewhere in this slot is shown busy (and cannot be picked); teachers
 * whose primary subject matches are suggested first. Clashes the server still detects (409) are
 * shown inline.
 */
export function CellDialog({ grid, dayOfWeek, period, lesson, onOpenChange }: CellDialogProps) {
  const { t } = useTranslation('timetable')
  const sectionId = grid.section.id
  const save = useSaveSlot(sectionId)
  const clear = useClearSlot(sectionId)
  const [conflict, setConflict] = useState<string | null>(null)
  const [teacherSearch, setTeacherSearch] = useState('')
  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<SlotFormValues>({
    resolver: zodResolver(slotSchema),
    defaultValues: {
      subjectId: lesson ? String(lesson.subject.id) : '',
      teacherId: lesson?.teacher ? String(lesson.teacher.id) : '',
      roomId: lesson?.room ? String(lesson.room.id) : '',
    },
  })
  const subjectId = useWatch({ control, name: 'subjectId' })

  const availability = useQuery({
    ...availabilityQuery({
      dayOfWeek,
      periodId: period.id,
      excludeSectionId: sectionId,
      subjectId: subjectId ? Number(subjectId) : undefined,
    }),
    placeholderData: keepPreviousData,
  })

  const subjectOptions = (grid.coverage ?? []).map((c) => ({
    value: c.subject.id,
    label:
      c.target !== null
        ? t('{{subject}} — {{scheduled}}/{{target}} scheduled', {
            subject: c.subject.name,
            scheduled: c.scheduled,
            target: c.target,
          })
        : t('{{subject}} — {{scheduled}} scheduled', { subject: c.subject.name, scheduled: c.scheduled }),
  }))

  const teacherOptions = useMemo<PickerOption[]>(() => {
    const data = availability.data
    if (!data) return []
    const suggested = new Set(data.suggestedTeacherIds)
    const term = teacherSearch.trim().toLocaleLowerCase()
    const rows = data.teachers
      .filter((tr) => !term || tr.name.toLocaleLowerCase().includes(term))
      .sort(
        (a, b) =>
          Number(suggested.has(b.id)) - Number(suggested.has(a.id)) ||
          Number(a.busy) - Number(b.busy) ||
          a.name.localeCompare(b.name),
      )
    return [
      { value: 'none', label: t('No teacher yet') },
      ...rows.map((tr) => ({
        value: String(tr.id),
        label: tr.name,
        disabled: tr.busy,
        description: tr.busy && tr.busyWith ? t('Teaching {{section}}', { section: tr.busyWith }) : undefined,
        badges: (
          <>
            {suggested.has(tr.id) ? (
              <Badge tone="brand" size="sm">
                {t('Teaches this subject')}
              </Badge>
            ) : null}
            {tr.busy ? (
              <Badge tone="danger" size="sm">
                {t('Busy')}
              </Badge>
            ) : (
              <Badge tone="success" size="sm">
                {t('Free')}
              </Badge>
            )}
          </>
        ),
      })),
    ]
  }, [availability.data, teacherSearch, t])

  const roomOptions = useMemo<PickerOption[]>(() => {
    const data = availability.data
    if (!data) return []
    return [
      { value: 'none', label: t('No room') },
      ...data.rooms.map((r) => ({
        value: String(r.id),
        label: r.name,
        disabled: r.busy,
        description:
          r.busy && r.busyWith ? t('Used by {{section}}', { section: r.busyWith }) : humanize(r.kind),
        badges: r.busy ? (
          <Badge tone="danger" size="sm">
            {t('Busy')}
          </Badge>
        ) : (
          <Badge tone="success" size="sm">
            {t('Free')}
          </Badge>
        ),
      })),
    ]
  }, [availability.data, t])

  const onSubmit = handleSubmit(async (values) => {
    setConflict(null)
    try {
      await save.mutateAsync({
        dayOfWeek,
        periodId: period.id,
        subjectId: Number(values.subjectId),
        teacherId: values.teacherId ? Number(values.teacherId) : null,
        roomId: values.roomId ? Number(values.roomId) : null,
      })
      toast.success(lesson ? t('Lesson updated') : t('Lesson added'))
      onOpenChange(false)
    } catch (e) {
      const err = toApiError(e)
      if (err.status === 409) {
        setConflict(err.message)
        void availability.refetch()
      } else {
        applyServerErrors(e, setError, FIELDS)
      }
    }
  })

  const onDelete = async () => {
    try {
      await clear.mutateAsync({ dayOfWeek, periodId: period.id })
      toast.success(t('Lesson removed'))
      onOpenChange(false)
    } catch (e) {
      setConflict(toApiError(e).message)
    }
  }

  const busy = isSubmitting || clear.isPending
  const title = lesson ? t('Edit lesson') : t('Add a lesson')

  return (
    <Dialog
      open
      onOpenChange={onOpenChange}
      size="lg"
      icon={<CalendarClock />}
      title={title}
      description={t('{{section}} · {{day}}, {{period}} ({{time}})', {
        section: grid.section.name,
        day: weekdayName(dayOfWeek),
        period: period.name,
        time: timeRange(period),
      })}
      dismissible={!busy}
      footer={
        <>
          {lesson ? (
            <Button
              variant="danger-soft"
              leftIcon={<Trash2 />}
              onClick={() => void onDelete()}
              loading={clear.isPending}
              disabled={isSubmitting}
              className="sm:mr-auto"
            >
              {t('Remove lesson')}
            </Button>
          ) : null}
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={busy}>
            {t('Cancel')}
          </Button>
          <Button type="submit" form="timetable-cell-form" loading={isSubmitting} disabled={clear.isPending}>
            {t('Save lesson')}
          </Button>
        </>
      }
    >
      <form id="timetable-cell-form" onSubmit={onSubmit} noValidate className="space-y-4">
        {conflict ? (
          <Alert tone="danger" title={t('This slot is not available')}>
            {conflict}
          </Alert>
        ) : null}
        <FormField
          label={t('Subject')}
          error={errors.subjectId?.message}
          required
          hint={t('Only subjects of the class curriculum can be scheduled.')}
        >
          <Select options={subjectOptions} placeholder={t('Choose a subject')} {...register('subjectId')} />
        </FormField>

        {availability.isPending ? (
          <LoadingState className="min-h-32" label={t('Checking who is free…')} />
        ) : availability.error ? (
          <Alert tone="warning">{t('Could not load teacher and room availability.')}</Alert>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex min-w-0 flex-col gap-1.5">
              <Controller
                control={control}
                name="teacherId"
                render={({ field }) => (
                  <FormField label={t('Teacher')} error={errors.teacherId?.message}>
                    <ResourcePicker
                      value={field.value || 'none'}
                      onValueChange={(v) => field.onChange(v === 'none' ? '' : v)}
                      options={teacherOptions}
                      aria-label={t('Teacher')}
                    />
                  </FormField>
                )}
              />
              <Input
                size="sm"
                leading={<Search />}
                value={teacherSearch}
                onChange={(e) => setTeacherSearch(e.target.value)}
                placeholder={t('Filter teachers')}
                aria-label={t('Filter teachers')}
              />
            </div>
            <Controller
              control={control}
              name="roomId"
              render={({ field }) => (
                <FormField label={t('Room')} error={errors.roomId?.message}>
                  <ResourcePicker
                    value={field.value || 'none'}
                    onValueChange={(v) => field.onChange(v === 'none' ? '' : v)}
                    options={roomOptions}
                    aria-label={t('Room')}
                  />
                </FormField>
              )}
            />
          </div>
        )}
      </form>
    </Dialog>
  )
}
